import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

export const vegaEmbedLoader = { load: () => import('vega-embed') };

export default function VegaChart({ spec }) {
  const containerRef = useRef(null);
  const viewRef = useRef(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!spec || !containerRef.current) return;

    let cancelled = false;

    setLoading(true);
    setError(null);

    (async () => {
      let vegaEmbed;
      try {
        ({ default: vegaEmbed } = await vegaEmbedLoader.load());
      } catch (err) {
        if (!cancelled) {
          setError(err.message || 'Failed to load chart library');
          setLoading(false);
        }
        return;
      }
      if (cancelled || !containerRef.current) return;

      try {
        const embedSpec = {
          ...spec,
          width: 'container',
          autosize: { type: 'fit', contains: 'padding' },
        };
        if (embedSpec.height == null) delete embedSpec.height;

        const result = await vegaEmbed(containerRef.current, embedSpec, {
          actions: false,
          renderer: 'svg',
        });
        if (cancelled) {
          result.view.finalize();
          return;
        }
        viewRef.current = result.view;
        setLoading(false);
      } catch (err) {
        if (!cancelled) {
          setError(err.message || 'Failed to render chart');
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
      if (viewRef.current) {
        viewRef.current.finalize();
        viewRef.current = null;
      }
    };
  }, [spec]);

  if (!spec) return null;

  return (
    <ChartContainer data-aid="vegaChart">
      {loading && !error && <LoadingText>Loading chart...</LoadingText>}
      {error && <ErrorText>Chart error: {error}</ErrorText>}
      <ChartWrapper $visible={!loading && !error} ref={containerRef} />
    </ChartContainer>
  );
}

VegaChart.propTypes = {
  spec: PropTypes.object,
};

const ChartContainer = styled.div`
  background: ${p => p.theme.colors.white};
  border: 1px solid ${p => p.theme.colors.gray300};
  border-radius: 8px;
  max-height: 400px;
  overflow: auto;
  padding: 12px;
  width: 100%;
`;

const ChartWrapper = styled.div`
  display: ${p => (p.$visible ? 'block' : 'none')};
  width: 100%;
`;

const LoadingText = styled.div`
  color: ${p => p.theme.colors.gray500};
  font-size: 13px;
  padding: 16px 0;
  text-align: center;
`;

const ErrorText = styled.div`
  color: ${p => p.theme.colors.red400};
  font-size: 13px;
  padding: 16px 0;
  text-align: center;
`;
